import { downloadFile } from '@/features/preinvest/_shared/downloadFile'
import { supabase } from '@/lib/supabase'
import { crmRequest, CrmApiError, errorMessage } from '../http'
import type {
  CommitPayload,
  ImportBatch,
  ImportBatchSummary,
  ImportMode,
  ImportSettingsPayload,
  ParsedFile,
  PreviewResult,
  RowInput,
  UndoResult,
} from './types'

/**
 * The Excel import of relations (spec-63 E6): `/api/v1/crm/imports`. The file is read on the
 * server and never stored; the browser keeps the rows while the user checks them, and the
 * server re-checks everything when they are saved.
 */
const BASE = '/api/v1/crm/imports'

const post = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) })

/** Read a file; a multipart upload, so not through `crmRequest` (it sets a JSON content type). */
export async function parseImportFile(
  file: File,
  mode: ImportMode,
  options: { sheet?: string | null; headerRow?: number | null } = {},
): Promise<ParsedFile> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`
  const body = new FormData()
  body.append('file', file)
  body.append('modus', mode)
  if (options.sheet) body.append('sheet', options.sheet)
  if (options.headerRow) body.append('header_row', String(options.headerRow))
  let response: Response
  try {
    response = await fetch(`${BASE}/parse`, { method: 'POST', headers, body })
  } catch {
    throw new CrmApiError('Geen verbinding met de server.', 0, 'network', null)
  }
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as Record<string, unknown> | null
    const message =
      typeof payload?.message === 'string' ? payload.message : errorMessage(payload, response.status)
    throw new CrmApiError(message, response.status, typeof payload?.error === 'string' ? payload.error : null, payload)
  }
  return (await response.json()) as ParsedFile
}

export function previewImport(body: {
  mode: ImportMode
  settings: ImportSettingsPayload
  rows: RowInput[]
  only?: number[]
}): Promise<PreviewResult> {
  return crmRequest('/imports/preview', post(body))
}

export function commitImport(body: CommitPayload): Promise<ImportBatch> {
  return crmRequest('/imports', post(body))
}

export function listImports(): Promise<ImportBatchSummary[]> {
  return crmRequest('/imports')
}

export function getImport(id: string): Promise<ImportBatch> {
  return crmRequest(`/imports/${id}`)
}

export function undoImport(id: string): Promise<UndoResult> {
  return crmRequest(`/imports/${id}/undo`, { method: 'POST' })
}

export function downloadImportTemplate(mode: ImportMode): Promise<void> {
  return downloadFile(
    `${BASE}/template.xlsx?modus=${mode}`,
    mode === 'contacten' ? 'VONK-relaties-sjabloon.xlsx' : 'VONK-organisaties-sjabloon.xlsx',
  )
}

export function downloadImportReport(id: string): Promise<void> {
  return downloadFile(`${BASE}/${id}/report.xlsx`, 'importrapport.xlsx')
}
